import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { bomKeys, getCurrentBom } from "@/api/bom";
import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

import type { DiagnosticTabProps } from "../DiagnosticSection";

/**
 * As quantidades da BOM vigente, abertas pelo quadro de perfil de uso do Ato 1
 * (#1345, F3).
 *
 * O Ato 1 não mostra R$: a revelação é do Ato 5, e quem chega por link direto
 * também não pode ver o número antes. Por isso aqui só há SKU, descrição,
 * métrica e quantidade; preço, total e ajuste ficam nos detalhes da BOM, que
 * nascem no Ato 5. Sem upload do Pricing App, a vigente é a calculada pela
 * execução, e a tela diz isso.
 */
export function BomQuantities(_props: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const current = useQuery({
    queryKey: bomKeys.current(projectId),
    queryFn: () => getCurrentBom(projectId),
    enabled: !!projectId,
    retry: false,
  });
  const bom = current.data;

  if (current.isLoading) {
    return <p className="text-[12px] text-[#596b85]">{t("Loading the BOM…")}</p>;
  }
  if (!bom) {
    return <p className="text-[12px] text-[#596b85]">{t("The project has no BOM yet.")}</p>;
  }

  return (
    <div className="space-y-4 text-[12px] text-[#08254c]">
      <p data-testid="bom-quantities-summary">
        {t("Version {{version}} · {{source}} · {{lines}} SKU lines · {{users}} licensed users", {
          version: bom.version,
          source: bom.persisted
            ? bom.source === "diagnostic_adjustment"
              ? t("adjusted in the Diagnostic")
              : // Nome do produto da SAP: igual nos três idiomas, não passa pela tradução.
                "Pricing App"
            : t("calculated by myGROW"),
          lines: formatCount(bom.totals.sku_lines),
          users: formatCount(bom.totals.licensed_users),
        })}
      </p>

      {!bom.persisted && (
        <p
          data-testid="bom-quantities-no-upload"
          className="rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-3 py-2 text-[#596b85]"
        >
          {t(
            "No Pricing App BOM was uploaded yet: these are the quantities calculated by myGROW in the last run.",
          )}
        </p>
      )}

      {/* Quadro branco, como as outras telas de detalhe; a tabela rola dentro dele. */}
      <div className="overflow-x-auto rounded-lg border border-[#e8ebf0] bg-white px-3 py-2">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#e8ebf0] text-left text-[10px] uppercase tracking-wide text-[#7b8799]">
              <th className="py-1 pr-2">{t("SKU")}</th>
              <th className="py-1 pr-2">{t("Description")}</th>
              <th className="py-1 pr-2">{t("Metric")}</th>
              <th className="py-1 text-right">{t("Quantity")}</th>
            </tr>
          </thead>
          <tbody>
            {bom.lines.map((line) => (
              <tr
                key={line.sku_number}
                data-testid={`bom-quantities-row-${line.sku_number}`}
                className={cn("border-b border-[#f2f4f7]", !line.in_total && "text-[#7b8799]")}
              >
                <td className="py-1 pr-2 font-mono">{line.sku_number}</td>
                <td className="py-1 pr-2">
                  {line.description}
                  {!line.in_total && (
                    <span className="ml-2 text-[11px]">{t("Only in myGROW · not in total")}</span>
                  )}
                </td>
                <td className="py-1 pr-2">{line.sales_unit_metric ?? "—"}</td>
                <td className="py-1 text-right tabular-nums">{formatCount(line.qty_current)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-[#596b85]">
        {t("Quantities only: prices and totals of the BOM are in Investment.")}
      </p>
    </div>
  );
}
